import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { NavLink } from 'react-router-dom';
import Layout from '../components/Layout';
import TrendVelocityChart from '../components/TrendVelocityChart';
import { mockTrends, mockAgentTasks, mockContentAssets } from '../data/index';
import {
  Compass, Zap, CheckSquare, Send, BarChart3, ArrowRight,
  TrendingUp, Users, Globe, DollarSign, Brain, Shield,
  Activity, Clock, FileText, Image as ImageIcon, Layers,
  Play, Pause, AlertTriangle, CheckCircle2, ChevronRight,
  Sparkles, Target, Search,
} from 'lucide-react';
import { ROUTE_PATHS } from '../lib/index';

const mockVelocityData = [
  45, 48, 52, 55, 58, 62, 65, 68, 72, 75,
  78, 80, 82, 85, 87, 88, 89, 90, 91, 92,
  92, 93, 93, 94, 94, 95, 95, 95, 94, 92
];

// ── AI Agent Panel ─────────────────────────────────────────────
const AGENTS = [
  { id: 'research',   name: 'Research Agent',   role: 'Scanning 847 sources',    status: 'running', progress: 72, color: 'text-cyan-400',    bar: 'bg-cyan-500' },
  { id: 'strategist', name: 'Strategist Agent',  role: 'Mapping content clusters', status: 'running', progress: 58, color: 'text-violet-400', bar: 'bg-violet-500' },
  { id: 'writer',     name: 'Writer Agent',      role: 'Drafting 3 blog posts',   status: 'idle',    progress: 100, color: 'text-emerald-400', bar: 'bg-emerald-500' },
];

// ── Pipeline Status ────────────────────────────────────────────
const PIPELINE_STAGES = [
  { label: 'Discovered', value: 42,  icon: Search,      color: 'text-cyan-400',    active: true  },
  { label: 'Generated',  value: 31,  icon: Zap,         color: 'text-violet-400',  active: true  },
  { label: 'In Approval', value: 8,  icon: CheckSquare, color: 'text-amber-400',   active: true  },
  { label: 'Published',  value: 24,  icon: Send,        color: 'text-emerald-400', active: false },
];

// ── Quick action cards ─────────────────────────────────────────
const QUICK_ACTIONS = [
  { label: 'Discover Trends',      sub: 'Find emerging opportunities',   icon: Compass,     to: ROUTE_PATHS.DISCOVER,   color: 'from-cyan-500/20 to-cyan-600/5 border-cyan-500/20 hover:border-cyan-400/40' },
  { label: 'Create Content Assets', sub: 'Generate publish-ready bundles', icon: Zap,        to: ROUTE_PATHS.GENERATE,   color: 'from-violet-500/20 to-violet-600/5 border-violet-500/20 hover:border-violet-400/40' },
  { label: 'Review Queue',         sub: '8 assets awaiting approval',    icon: CheckSquare, to: ROUTE_PATHS.APPROVAL,   color: 'from-amber-500/20 to-amber-600/5 border-amber-500/20 hover:border-amber-400/40' },
  { label: 'Analytics & ROI',      sub: 'Track content performance',     icon: BarChart3,   to: ROUTE_PATHS.ANALYTICS,  color: 'from-emerald-500/20 to-emerald-600/5 border-emerald-500/20 hover:border-emerald-400/40' },
];

// ── Top trending from discovery ────────────────────────────────
const topTrends = [...mockTrends].sort((a, b) => b.popularity - a.popularity).slice(0, 4);

const Dashboard: React.FC = () => {
  const [selectedTrendId, setSelectedTrendId] = useState<string>(mockTrends[0].id);
  const selectedTrend = mockTrends.find(t => t.id === selectedTrendId) || mockTrends[0];
  const pendingApproval = mockContentAssets.filter(c => c.status === 'pending-review').length;

  return (
    <Layout>
      <div className="px-4 sm:px-6 py-6 space-y-6 max-w-[1400px] mx-auto">

        {/* ── Top KPI Bar ──────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-3"
        >
          {[
            { label: 'Trends Identified',   value: '342',    delta: '+18%', icon: Search,       color: 'text-cyan-400',    bg: 'bg-cyan-500/10 border-cyan-500/20' },
            { label: 'Topics Generated',     value: '127',    delta: '+24%', icon: Zap,          color: 'text-violet-400',  bg: 'bg-violet-500/10 border-violet-500/20' },
            { label: 'Est. Reach',           value: '475K',   delta: '+12%', icon: Globe,        color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20' },
            { label: 'Content ROI',          value: '9.8×',   delta: '+8%',  icon: DollarSign,   color: 'text-amber-400',   bg: 'bg-amber-500/10 border-amber-500/20' },
          ].map((k, i) => (
            <motion.div
              key={k.label}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: i * 0.06 }}
              className={`rounded-2xl border p-4 ${k.bg}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <k.icon className={`w-3.5 h-3.5 ${k.color}`} />
                <span className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">{k.label}</span>
              </div>
              <div className="flex items-end justify-between">
                <p className={`text-3xl font-bold tracking-tight ${k.color}`}>{k.value}</p>
                <span className="text-[11px] text-emerald-400 font-medium">{k.delta}</span>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* ── Main Grid ─────────────────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

          {/* Left: Pipeline Status + Agents (2 cols) */}
          <div className="lg:col-span-2 space-y-5">

            {/* Pipeline Status Funnel */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="bg-slate-900/60 border border-slate-800/60 rounded-2xl p-5"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Activity className="w-3.5 h-3.5 text-violet-400" />
                  <h2 className="text-sm font-semibold text-slate-200">Pipeline Status</h2>
                </div>
                <NavLink to={ROUTE_PATHS.GENERATE} className="flex items-center gap-1 text-[11px] text-violet-400 hover:text-violet-300 transition-colors">
                  View Pipeline <ArrowRight className="w-3 h-3" />
                </NavLink>
              </div>
              <div className="grid grid-cols-4 gap-3">
                {PIPELINE_STAGES.map((stage, i) => (
                  <div key={stage.label} className="text-center">
                    <div className={`mx-auto w-10 h-10 rounded-xl flex items-center justify-center mb-2 ${stage.active ? 'bg-slate-800/80' : 'bg-slate-800/40'}`}>
                      <stage.icon className={`w-4.5 h-4.5 ${stage.color}`} />
                    </div>
                    <p className={`text-2xl font-bold tracking-tight ${stage.color}`}>{stage.value}</p>
                    <p className="text-[10px] font-medium text-slate-600 mt-0.5 uppercase tracking-wide">{stage.label}</p>
                    {i < PIPELINE_STAGES.length - 1 && (
                      <div className="hidden md:block absolute" />
                    )}
                  </div>
                ))}
              </div>
              {/* Flow connector */}
              <div className="mt-4 flex items-center gap-1">
                {PIPELINE_STAGES.map((s, i) => (
                  <React.Fragment key={s.label}>
                    <div className={`flex-1 h-1 rounded-full ${s.active ? 'bg-violet-500/40' : 'bg-slate-800'}`} />
                    {i < PIPELINE_STAGES.length - 1 && (
                      <div className="w-1.5 h-1.5 rounded-full bg-slate-700 flex-shrink-0" />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </motion.div>

            {/* Active AI Agent Panel */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-slate-900/60 border border-slate-800/60 rounded-2xl p-5"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Brain className="w-3.5 h-3.5 text-violet-400" />
                  <h2 className="text-sm font-semibold text-slate-200">Active AI Agent Panel</h2>
                </div>
                <span className="flex items-center gap-1.5 text-[10px] font-medium text-emerald-400">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  2 RUNNING
                </span>
              </div>
              <div className="space-y-4">
                {AGENTS.map(agent => (
                  <div key={agent.id} className="flex items-center gap-4">
                    <div className={`w-8 h-8 rounded-xl bg-slate-800/80 flex items-center justify-center flex-shrink-0`}>
                      <Sparkles className={`w-4 h-4 ${agent.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                      <span className="text-[12px] font-semibold text-slate-300">{agent.name}</span>
                        <span className={`text-[10px] font-medium ${agent.status === 'running' ? 'text-emerald-400' : 'text-slate-500'}`}>
                          {agent.status === 'running' ? '● Running' : '○ Idle'}
                        </span>
                      </div>
                      <p className="text-[10px] text-slate-500 mb-1.5">{agent.role}</p>
                      <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${agent.progress}%` }}
                          transition={{ duration: 1, delay: 0.3 }}
                          className={`h-full rounded-full ${agent.bar}`}
                        />
                      </div>
                    </div>
                    <span className={`text-[11px] font-bold ${agent.color} w-8 text-right flex-shrink-0`}>{agent.progress}%</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Velocity Chart */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
            >
              <TrendVelocityChart
                topicTitle={selectedTrend.name}
                velocityData={mockVelocityData}
              />
            </motion.div>
          </div>

          {/* Right: Quick Actions + Top Trends */}
          <div className="space-y-5">

            {/* Quick Actions */}
            <motion.div
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h2 className="text-[9px] font-bold uppercase tracking-widest text-slate-600 mb-3">Quick Actions</h2>
              <div className="space-y-2">
                {QUICK_ACTIONS.map(a => (
                  <NavLink
                    key={a.to}
                    to={a.to}
                    className={`flex items-center gap-3 p-3 rounded-xl border bg-gradient-to-r ${a.color} transition-all group`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-slate-900/60 flex items-center justify-center flex-shrink-0">
                      <a.icon className="w-4 h-4 text-slate-300" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[12px] font-semibold text-slate-200 leading-tight">{a.label}</p>
                      <p className="text-[10px] text-slate-500">{a.sub}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-slate-400 transition-colors flex-shrink-0" />
                  </NavLink>
                ))}
              </div>
            </motion.div>

            {/* Pending Approval Alert */}
            {pendingApproval > 0 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 }}
                className="bg-amber-500/8 border border-amber-500/20 rounded-2xl p-4"
              >
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[12px] font-semibold text-amber-300">{pendingApproval} assets pending review</p>
                    <p className="text-[10px] text-slate-500 mt-0.5">Brand compliance check required</p>
                  </div>
                  <NavLink
                    to={ROUTE_PATHS.APPROVAL}
                    className="flex-shrink-0 px-2.5 py-1 bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/20 rounded-lg text-[11px] font-semibold text-amber-400 transition-colors"
                  >
                    Review
                  </NavLink>
                </div>
              </motion.div>
            )}

            {/* Top Trends List */}
            <motion.div
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.35 }}
            >
              <div className="flex items-center justify-between mb-3">
              <h2 className="text-[9px] font-bold uppercase tracking-widest text-slate-600">Top Trends</h2>
                <NavLink to={ROUTE_PATHS.DISCOVER} className="text-[11px] text-violet-400 hover:text-violet-300 flex items-center gap-0.5 transition-colors">
                  Discover all <ArrowRight className="w-3 h-3" />
                </NavLink>
              </div>
              <div className="space-y-2">
                {topTrends.map((t, i) => (
                  <button
                    key={t.id}
                    onClick={() => setSelectedTrendId(t.id)}
                    className={`w-full flex items-start gap-3 p-3 rounded-xl border text-left transition-all ${
                      selectedTrendId === t.id
                        ? 'bg-violet-500/10 border-violet-500/25'
                        : 'bg-slate-900/40 border-slate-800/60 hover:border-slate-700/60'
                    }`}
                  >
                    <span className="text-[10px] font-bold text-slate-600 w-4 flex-shrink-0 mt-0.5">#{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-[12px] font-semibold text-slate-200 leading-snug line-clamp-1">{t.name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full border ${
                          t.status === 'trending' ? 'text-violet-400 bg-violet-500/10 border-violet-500/20' :
                          t.status === 'emerging' ? 'text-cyan-400 bg-cyan-500/10 border-cyan-500/20' :
                          'text-green-400 bg-green-500/10 border-green-500/20'
                        }`}>
                          {t.status}
                        </span>
                        <span className="text-[10px] text-emerald-400 font-medium">+{t.growthRate}%</span>
                      </div>
                    </div>
                    <TrendingUp className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${selectedTrendId === t.id ? 'text-violet-400' : 'text-slate-600'}`} />
                  </button>
                ))}
              </div>
            </motion.div>

            {/* Bundle Metrics */}
            <motion.div
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-slate-900/60 border border-slate-800/60 rounded-2xl p-4"
            >
              <div className="flex items-center gap-2 mb-3">
                <Target className="w-4 h-4 text-emerald-400" />
                <h2 className="text-[12px] font-semibold text-slate-200">Bundle Metrics</h2>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'SEO Score',     value: '91',  suffix: '/100', color: 'text-cyan-400' },
                  { label: 'Social Assets', value: '24',  suffix: ' pcs', color: 'text-violet-400' },
                  { label: 'Brand Align',   value: '88%', suffix: '',     color: 'text-emerald-400' },
                  { label: 'AI Confidence', value: '94%', suffix: '',     color: 'text-amber-400' },
                ].map(m => (
                  <div key={m.label} className="bg-slate-800/40 rounded-xl p-2.5 text-center">
                    <p className={`text-lg font-bold ${m.color}`}>{m.value}<span className="text-[10px] text-slate-600">{m.suffix}</span></p>
                    <p className="text-[9px] text-slate-600 mt-0.5">{m.label}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>

      </div>
    </Layout>
  );
};

export default Dashboard;
